import type { PlayerId } from '../types';

/**
 * Player color scheme for visual distinction
 * Supports 2-4 players with distinct, accessible colors
 */
export const PLAYER_COLORS: Record<number, { primary: number; text: string; name: string }> = {
  1: { primary: 0x2196F3, text: '#ffffff', name: 'Blue' },   // Player 1: Blue
  2: { primary: 0xF44336, text: '#ffffff', name: 'Red' },    // Player 2: Red
  3: { primary: 0x4CAF50, text: '#ffffff', name: 'Green' },  // Player 3: Green
  4: { primary: 0xFFC107, text: '#000000', name: 'Yellow' }, // Player 4: Yellow
};

/**
 * Get color for a specific player
 */
export function getPlayerColor(playerId: PlayerId): { primary: number; text: string; name: string } {
  const color = PLAYER_COLORS[playerId];
  if (!color) {
    // Default to gray for unknown players
    return { primary: 0x9E9E9E, text: '#ffffff', name: 'Gray' };
  }
  return color;
}

/**
 * Get a lighter version of player color (for backgrounds, highlights)
 */
export function getPlayerColorLight(playerId: PlayerId): number {
  const color = getPlayerColor(playerId).primary;
  // Simple lightening: increase each RGB component
  const r = Math.min(255, ((color >> 16) & 0xFF) + 40);
  const g = Math.min(255, ((color >> 8) & 0xFF) + 40);
  const b = Math.min(255, (color & 0xFF) + 40);
  return (r << 16) | (g << 8) | b;
}

/**
 * Get a darker version of player color (for borders, shadows)
 */
export function getPlayerColorDark(playerId: PlayerId): number {
  const color = getPlayerColor(playerId).primary;
  // Simple darkening: decrease each RGB component
  const r = Math.max(0, ((color >> 16) & 0xFF) - 40);
  const g = Math.max(0, ((color >> 8) & 0xFF) - 40);
  const b = Math.max(0, (color & 0xFF) - 40);
  return (r << 16) | (g << 8) | b;
}
